import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import Image from '../../components/images/Image';
import { apiRequest, awardArt, shortDate } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './CompanyTournament.css';
import './HostEvent.css';
import '../../components/common/EventTags.css';

/* The wall of fame: everything this company ever won, and everything ever won at its evenings.

   ★★ **Grouped by EVENING, newest first** — and that is the whole design decision. A flat list of
   prizes sorted by name is an inventory; a wall is "the night of 4 September, and these five
   people". The heading is the event, the rows under it are what was handed out there, and the
   picture beside each is the prize's own.

   ★ **Four legs reach this wall**, and the server owns them (``wall_of_fame``): every prize handed
   out at an event the company organised, whoever won it — plus, WITHIN THIS BRAND, a prize one of
   their teams won, a prize the company itself won, and a prize with one of their people's names on
   it. Those last three exist for the events a customer takes part in without organising: a Bravos
   **Lab** carries no organiser at all, so the first leg cannot see it, and a Lab is exactly where
   an engagement customer's people win things. The brand scope is what keeps a Hercules Trophy day
   from 2017 off a screen that is about this year's programme.

   ★ An evening SOMEBODY ELSE organised carries a pill saying so; an evening with no organiser
   recorded — every Lab, and every company evening from before that column was filled in — carries
   none, because that is not the same statement.

   ★ **Only what was revealed.** An unannounced winner never reaches any reader, here least of all:
   a wall is a memory, and the ceremony is what makes something a memory.

   ★ The page is open to every colleague of the company, not only the admin whose home carries the
   tile — everything on it was announced out loud in a room and is already on the winners' own
   profiles. The gate is the community wall's; see ``_wall_gate``. */

const KIND_ICONS = { person: 'user', team: 'users', company: 'building', activity: 'flag-checkered' };

/* Where a winner's own page is. A person and a team and a company each have one; our crew award
   points at a game, which has no page a customer may open — so it is drawn plain. */
const winnerLink = (winner) => {
  if (!winner || !winner.id) return null;
  if (winner.kind === 'person') return `/u/${winner.id}`;
  if (winner.kind === 'team') return `/public-team/${winner.id}`;
  if (winner.kind === 'company') return `/public-company/${winner.id}`;
  return null;
};

const WallOfFame = () => {
  const { t, i18n } = useTranslation(['common', 'awards']);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [kind, setKind] = useState('all');

  useEffect(() => {
    apiRequest('GET', '/company/wall-of-fame')
      .then(setData)
      .catch(() => navigate('/member-home'));
  }, [navigate]);

  // Memoised for the same reason HostEvent memoises its surveys: a fresh [] on every render makes
  // the grouping below recompute every time, which on a wall of a hundred prizes is visible.
  const awards = useMemo(() => (data && data.awards) || [], [data]);

  /* The wall itself: the rows the chips left, folded into evenings. The server already sorted them
     newest first, so the groups come out in order by insertion and nothing is sorted twice. */
  const evenings = useMemo(() => {
    const shown = kind === 'all' ? awards : awards.filter((a) => a.winner.kind === kind);
    const out = [];
    const index = {};
    shown.forEach((row) => {
      let group = index[row.event_id];
      if (!group) {
        group = { id: row.event_id, name: row.event_name, date: row.event_date,
                  elsewhere: row.elsewhere, prizes: [] };
        index[row.event_id] = group;
        out.push(group);
      }
      group.prizes.push(row);
    });
    return out;
  }, [awards, kind]);

  if (data === null) {
    return (
      <Base activeNav="home">
        <Card><p className="ct-field__hint">{t('pleaseWait')}</p></Card>
      </Base>
    );
  }

  const summary = data.summary || { total: 0, events: 0, people: 0 };
  /* The chips only offer what is actually ON the wall: a company that has never given a prize to a
     person should not be offered a filter that empties the page. */
  const kinds = ['all', 'person', 'team', 'company']
    .filter((k) => k === 'all' || awards.some((a) => a.winner.kind === k));

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="trophy" theme="primary" className="me-2" />
          {t('wallOfFame')}
        </Title>
        <p className="bravos-q__hint mb-2">{t('wallOfFameNote')}</p>

        {summary.total > 0 && (
          <div className="hv-counts">
            {[['wallCountPrizes', summary.total], ['wallCountEvents', summary.events],
              ['wallCountPeople', summary.people]].map(([key, n]) => (
                <div key={key} className="hv-count">
                  <span className="hv-count__n">{n}</span>
                  <span className="hv-count__label">{t(key)}</span>
                </div>
            ))}
          </div>
        )}

        {kinds.length > 2 && (
          <div className="etags etags--pick etags--sm mt-2">
            {kinds.map((k) => (
              <button
                key={k}
                type="button"
                className={`etag etag--btn ${kind === k ? 'is-on' : ''}`}
                onClick={() => setKind(k)}
              >
                {k !== 'all' && <><Icon name={KIND_ICONS[k]} />{' '}</>}
                {k === 'all' ? t('wallAll') : t(`awardKind_${k}`)}
              </button>
            ))}
          </div>
        )}

        {summary.total === 0 && (
          <>
            <p className="ce-note">{t('wallEmpty')}</p>
            {/* The fix is one screen away, and it is not this one: a prize is made on the evening
                it is handed out at. */}
            <button
              type="button"
              className="bravos-submit bravos-submit--ghost"
              onClick={() => navigate('/company-events')}
            >
              <Icon name="calendar-star" /> {t('wallEmptyGoToEvents')}
            </button>
          </>
        )}

        {evenings.map((evening) => (
          <div key={evening.id}>
            <p className="ct-pick__label mt-3">
              {evening.name}
              {evening.date && <span className="bravos-themerow__when">{' · '}{shortDate(evening.date, i18n.language)}</span>}
              {/* ★ Said on the heading and not on every row: a prize won at somebody ELSE's event
                  is a different kind of memory from one the company handed out itself.

                  ★★ And ONLY when the server says somebody else organised it — never on the
                  absence of an organiser. That column is empty on every Hercules day and on every
                  company evening from before it was filled in, so "won elsewhere" out of a missing
                  value would label a customer's own day as a stranger's. See `elsewhere`. */}
              {evening.elsewhere && (
                <span className="bravos-themerow__live">{t('wallWonElsewhere')}</span>
              )}
            </p>

            {evening.prizes.map((row) => {
              const to = winnerLink(row.winner);
              const body = (
                <>
                  <span
                    className="bravos-themerow__img"
                    style={{ backgroundImage: `url("${awardArt(row.name, row.image)}")`,
                             backgroundSize: 'contain', backgroundRepeat: 'no-repeat' }}
                  />
                  <span className="bravos-themerow__body">
                    <span className="bravos-themerow__name">
                      {t(row.name, { ns: 'awards', defaultValue: row.name })}
                    </span>
                    <span className="bravos-themerow__when">
                      <Icon name={KIND_ICONS[row.winner.kind] || 'user'} />
                      {' '}
                      {row.winner.name}
                      {row.description ? ` · ${row.description}` : ''}
                    </span>
                  </span>
                  {row.winner.photo_url && (
                    <Image
                      url={row.winner.photo_url}
                      size="2.4rem"
                      className="circle"
                      contain={row.winner.kind === 'company'}
                    />
                  )}
                  {to && <Icon name="angle-right" />}
                </>
              );
              return to ? (
                <Link key={row.id} to={to} className="bravos-themerow">{body}</Link>
              ) : (
                <div key={row.id} className="bravos-themerow">{body}</div>
              );
            })}
          </div>
        ))}
      </Card>
    </Base>
  );
};

export default WallOfFame;
